// Checklists.tsx - Complete checklist based on actual system implementation

import React, { useState, useEffect } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { CheckCircle, AlertTriangle, InfoIcon, FileText, BadgeCheck, Clock, Download, DownloadCloud, Building, Shield, Users, Gavel, TrendingUp, Search, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import DashboardTopbar from "@/components/layout/DashboardTopbar";

interface CheckItem {
  id: string;
  name: string;
  description: string;
  category: string;
  documents: string[];
  conditions?: string[];
}

const Checklists = () => {
  const [showAllChecks, setShowAllChecks] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");

  // =====================================
  // INDEPENDENT DOCUMENT CHECKS
  // =====================================
  const independentDocumentChecks = {
    basicCompliance: [
      {
        id: 'audit_title',
        name: 'Audit Title',
        description: 'Checking for heading "Independent Auditor\'s Report"',
        category: 'Basic Compliance',
        documents: ['Audit Report']
      },
      {
        id: 'address_to_members',
        name: 'Address to Members',
        description: 'Verifying format "To the Members of [Company Name]"',
        category: 'Basic Compliance',
        documents: ['Audit Report']
      },
      {
        id: 'audit_report_date',
        name: 'Audit Report Date',
        description: 'Checking if the date matches user input',
        category: 'Basic Compliance',
        documents: ['Audit Report']
      },
      {
        id: 'company_name_consistency',
        name: 'Company Name Consistency',
        description: 'Checking for consistent company naming throughout',
        category: 'Basic Compliance',
        documents: ['Audit Report']
      },
      {
        id: 'pkf_signature_block',
        name: 'PKF Signature Block',
        description: 'Verifying complete signature with registration number, partner details, UDIN',
        category: 'Basic Compliance',
        documents: ['Audit Report']
      }
    ],
    statementTypeCompliance: [
      {
        id: 'financial_statement_type_check',
        name: 'Financial Statement Type Verification',
        description: 'Ctrl+F search to count instances of Standalone/Consolidated financial statements',
        category: 'Statement Type Compliance',
        documents: ['Audit Report'],
        conditions: ['Report Type = Standalone/Consolidated']
      },
      {
        id: 'consolidated_financial_statements',
        name: 'Consolidated Financial Statements Verification',
        description: 'Verifies the consolidated financial statements document',
        category: 'Statement Type Compliance',
        documents: ['CFS']
      },
      {
        id: 'standalone_consolidated_wording',
        name: 'Standalone/Consolidated Wording',
        description: 'Verifying consistency throughout document',
        category: 'Statement Type Compliance',
        documents: ['Audit Report'],
        conditions: ['Report Type = Standalone/Consolidated']
      },
      {
        id: 'financial_units_check',
        name: 'Financial Statement Units Verification',
        description: 'Checking for units (Rs. in lakhs/crores/thousands) in financial statements',
        category: 'Statement Type Compliance',
        documents: ['Audit Report'],
        conditions: ['Report Type = Standalone/Consolidated']
      }
    ],
    auditStandards: [
      {
        id: 'key_audit_matters',
        name: 'Key Audit Matters',
        description: 'Ensuring at least one Key Audit Matter is present',
        category: 'Audit Standards',
        documents: ['Audit Report']
      },
      {
        id: 'audit_trail_software',
        name: 'Audit-Trail Accounting Software',
        description: 'Checking for required disclosure',
        category: 'Technology Compliance',
        documents: ['Audit Report']
      },
      {
        id: 'audit_opinion_type_check',
        name: 'Audit Opinion Type Verification',
        description: 'Verifies instances of specific audit opinion type (Adverse/Qualified) when applicable',
        category: 'Opinion Type Compliance',
        documents: ['Audit Report'],
        conditions: ['Opinion Type = Adverse/Qualified']
      }
    ],
    regulatoryCompliance: [
      {
        id: 'section_197_16',
        name: 'Section 197(16)',
        description: 'Verifying presence of required disclosure',
        category: 'Regulatory Compliance',
        documents: ['Audit Report']
      },
      {
        id: 'brsr_brr_check',
        name: 'BRSR/BRR Check',
        description: 'Verifying BRSR for top 1000/500 listed companies or BRR for others',
        category: 'Sustainability Reporting',
        documents: ['Audit Report'],
        conditions: ['Company Status = Listed']
      },
      {
        id: 'profit_loss_consistency',
        name: 'Profit/Loss Consistency Check',
        description: 'Verifying if the profit/loss mentioned in audit opinion matches user selection',
        category: 'Financial Results Compliance',
        documents: ['Audit Report']
      }
    ],
    caroCompliance: [
      {
        id: 'benami_property_clause',
        name: 'Benami Property Clause',
        description: 'Verifying presence of Benami Property Clause in CARO annexure',
        category: 'CARO Regulatory Compliance',
        documents: ['CARO Annexure A'],
        conditions: ['Report Type = Standalone/Normal']
      },
      {
        id: 'caro_clause_xiii_related_party',
        name: 'CARO Clause (xiii) - Related Party Transactions Compliance',
        description: 'Verifying CARO clause (xiii) addresses related party transactions compliance with Sections 177 & 188',
        category: 'Related Party Compliance',
        documents: ['CARO Annexure A'],
        conditions: ['Report Type = Standalone/Normal']
      },
      {
        id: 'caro_clause_vi_cost_auditor',
        name: 'CARO Clause (vi) - Cost Records Maintenance',
        description: 'Verifying CARO clause (vi) addresses cost records maintenance under Section 148 when Cost Auditor is appointed',
        category: 'Cost Audit Compliance',
        documents: ['CARO Annexure A'],
        conditions: ['Report Type = Standalone/Normal', 'Has Cost Auditor = Yes']
      },
      {
        id: 'caro_clause_vii_a_statutory_dues',
        name: 'CARO Clause (vii)(a) - Regular Deposit of Undisputed Statutory Dues',
        description: 'Verifying CARO clause (vii)(a) confirms regular deposit of undisputed statutory dues including GST, PF, ESI, Income Tax, Customs, Excise, Cess',
        category: 'Statutory Dues Compliance',
        documents: ['CARO Annexure A'],
        conditions: ['Report Type = Standalone/Normal']
      },
      {
        id: 'caro_clause_vii_b_disputed_dues',
        name: 'CARO Clause (vii)(b) - Disputed Statutory Dues Disclosure',
        description: 'Verifying CARO clause (vii)(b) properly discloses disputed statutory dues status with tabular disclosure if applicable',
        category: 'Statutory Dues Compliance',
        documents: ['CARO Annexure A'],
        conditions: ['Report Type = Standalone/Normal']
      },
      {
        id: 'internal_auditor_clause_xiv',
        name: 'Internal Auditor Clause (xiv)',
        description: 'Checking if CARO clause (xiv) addresses internal auditor appointment requirements',
        category: 'Internal Auditor Compliance',
        documents: ['CARO Annexure A'],
        conditions: ['Report Type = Standalone/Normal', 'Has Internal Auditor = Yes']
      }
    ]
  };

  // =====================================
  // SINGLE DOCUMENT CHECKS (Legacy)
  // =====================================
  const singleDocumentChecks = {
    auditReport: [
      {
        id: 'audit_title',
        name: 'Independent Auditor\'s Report Title',
        description: 'Verifies the exact heading "Independent Auditor\'s Report" is present',
        category: 'Basic Compliance',
        documents: ['Audit Report']
      },
      {
        id: 'company_format',
        name: 'Address to Members Format',
        description: 'Checks for exact text "To the Members of [Company Name]" format',
        category: 'Basic Compliance',
        documents: ['Audit Report'],

      },
      {
        id: 'signature_date',
        name: 'PKF Signature Block & Date',
        description: 'Validates PKF Sridhar & Santhanam LLP signature block with correct audit date',
        category: 'Basic Compliance',
        documents: ['Audit Report'],

      },
      {
        id: 'financial_statements_type',
        name: 'Financial Statements Type Consistency',
        description: 'Ensures all instances of "financial statements" are properly qualified (Standalone/Consolidated)',
        category: 'Statement Type Compliance',
        documents: ['Audit Report'],
        conditions: ['Report Type ≠ Normal'],

      },
      {
        id: 'brsr_brr',
        name: 'BRSR/BRR Disclosure Check',
        description: 'Verifies appropriate Business Responsibility reporting (BRSR for Top 1000/500, BRR for others)',
        category: 'Sustainability Reporting',
        documents: ['Audit Report'],
        conditions: ['Listed Company'],

      },
      {
        id: 'key_audit_matter',
        name: 'Key Audit Matters Disclosure',
        description: 'Checks for mention of "Key Audit Matters" or "KAM" in the audit report',
        category: 'Audit Standards',
        documents: ['Audit Report'],

      },
      {
        id: 'audit_trail_software',
        name: 'Audit Trail Software Disclosure',
        description: 'Verifies mention of accounting software with audit trail recording feature',
        category: 'Technology Compliance',
        documents: ['Audit Report'],

      },
      {
        id: 'section_197_reference',
        name: 'Section 197(16) Reference',
        description: 'Checks for specific reference to Section 197(16) regarding director remuneration',
        category: 'Regulatory Compliance',
        documents: ['Audit Report'],

      },
      {
        id: 'company_name_consistency',
        name: 'Company Name Consistency',
        description: 'Verifies consistent use of company name throughout the audit report',
        category: 'Basic Compliance',
        documents: ['Audit Report'],

      },
      {
        id: 'consolidated_wording',
        name: 'Consolidated Statements Wording',
        description: 'Ensures consistent use of "consolidated" qualifier throughout the report',
        category: 'Consolidated Compliance',
        documents: ['Audit Report'],
        conditions: ['Consolidated Report'],

      }
    ],
    caroAnnexure: [
      {
        id: 'clause_20',
        name: 'CARO Clauses (i-xx) - Standalone Complete',
        description: 'Verifies all 20 mandatory CARO clauses (i) to (xx) are present for standalone reports',
        category: 'CARO Standalone',
        documents: ['CARO Annexure A'],
        conditions: ['Standalone/Normal Report'],

      },
      {
        id: 'clause_21',
        name: 'CARO Clause (xxi) - Consolidated Only',
        description: 'Verifies only clause (xxi) is present for consolidated reports',
        category: 'CARO Consolidated',
        documents: ['CARO Annexure A'],
        conditions: ['Consolidated Report'],

      },
      {
        id: 'benami_property_clause',
        name: 'CARO Clause (i)(e) - Benami Property',
        description: 'Checks clause (i)(e) for Benami property proceedings disclosure',
        category: 'CARO Regulatory Compliance',
        documents: ['CARO Annexure A'],
        conditions: ['Standalone/Normal Report'],

      },
      {
        id: 'caro_clause_xiii_related_party',
        name: 'CARO Clause (xiii) - Related Party Transactions',
        description: 'Verifies clause (xiii) compliance with Sections 177 and 188 for related party transactions',
        category: 'Related Party Compliance',
        documents: ['CARO Annexure A'],
        conditions: ['Standalone/Normal Report'],

      },
      {
        id: 'internal_auditor_clause_xiv',
        name: 'CARO Clause (xiv) - Internal Auditor',
        description: 'Confirms clause (xiv) properly discloses internal auditor appointment status',
        category: 'Internal Auditor Compliance',
        documents: ['CARO Annexure A'],
        conditions: ['Has Internal Auditor = Yes'],

      },
      {
        id: 'caro_clause_vi_cost_auditor',
        name: 'CARO Clause (vi) - Cost Records Maintenance',
        description: 'Verifies clause (vi) addresses cost records maintenance under Section 148',
        category: 'Cost Audit Compliance',
        documents: ['CARO Annexure A'],
        conditions: ['Has Cost Auditor = Yes'],

      },
      {
        id: 'caro_clause_vii_a_statutory_dues',
        name: 'CARO Clause (vii)(a) - Regular Statutory Dues',
        description: 'Checks clause (vii)(a) for regular deposit of undisputed statutory dues',
        category: 'Statutory Dues Compliance',
        documents: ['CARO Annexure A'],
        conditions: ['Standalone/Normal Report'],

      },
      {
        id: 'caro_clause_vii_b_disputed_dues',
        name: 'CARO Clause (vii)(b) - Disputed Statutory Dues',
        description: 'Verifies clause (vii)(b) proper disclosure of disputed statutory dues',
        category: 'Statutory Dues Compliance',
        documents: ['CARO Annexure A'],
        conditions: ['Standalone/Normal Report'],

      },
      {
        id: 'caro_nbfc_exemptions',
        name: 'NBFC CARO Clause Exemptions',
        description: 'Validates proper NBFC exemptions for clauses (iii)(a) and (iii)(e)',
        category: 'NBFC Compliance',
        documents: ['CARO Annexure A'],
        conditions: ['NBFC = Yes'],

      }
    ]
  };

  // =====================================
  // RESULTS AUDIT CHECKS
  // =====================================
  const resultsAuditChecks = {
    basicCompliance: [
      {
        id: 'rs_in_units',
        name: 'Rs in units',
        description: 'Check for currency units mention in financial statements',
        category: 'Basic Compliance',
        documents: ['Results Report']
      },
      {
        id: 'period_ended',
        name: 'Period Ended',
        description: 'Verify the period ended date is correctly mentioned',
        category: 'Basic Compliance',
        documents: ['Results Report']
      },
      {
        id: 'face_value_share',
        name: 'Face Value of Share',
        description: 'Check face value per share is mentioned correctly',
        category: 'Basic Compliance',
        documents: ['Results Report']
      },
      {
        id: 'basic_diluted_eps',
        name: 'Basic and Diluted EPS',
        description: 'Verify basic and diluted earnings per share are calculated and disclosed',
        category: 'Basic Compliance',
        documents: ['Results Report']
      },
      {
        id: 'exceptional_items_note',
        name: 'Exceptional Items Note',
        description: 'Check for proper disclosure of exceptional items with explanatory notes',
        category: 'Basic Compliance',
        documents: ['Results Report']
      },
      {
        id: 'not_annualised_eps',
        name: 'Not Annualised EPS',
        description: 'Verify EPS is not annualised for quarterly results',
        category: 'Basic Compliance',
        documents: ['Results Report']
      },
      {
        id: 'audit_committee_board_approval',
        name: 'Audit Committee Board Approval',
        description: 'Check for audit committee and board approval of quarterly results',
        category: 'Basic Compliance',
        documents: ['Results Report']
      },
      {
        id: 'interim_dividend_details',
        name: 'Interim Dividend Details',
        description: 'Verify interim dividend declaration and payment details',
        category: 'Basic Compliance',
        documents: ['Results Report']
      },
      {
        id: 'major_events_notes',
        name: 'Major Events Notes',
        description: 'Check for disclosure of major events affecting the quarter',
        category: 'Basic Compliance',
        documents: ['Results Report']
      },
      {
        id: 'accounting_policy_changes',
        name: 'Accounting Policy Changes',
        description: 'Verify disclosure of any changes in accounting policies',
        category: 'Basic Compliance',
        documents: ['Results Report']
      }
    ],
    auditReportCompliance: [
      {
        id: 'audit_report_addressee',
        name: 'Audit Report Addressee',
        description: 'Check audit report is addressed to the members of the company',
        category: 'Audit Report Compliance',
        documents: ['Audit Report']
      },
      {
        id: 'independent_auditors_report',
        name: 'Independent Auditors Report',
        description: 'Verify the report is titled as Independent Auditors Report',
        category: 'Audit Report Compliance',
        documents: ['Audit Report']
      },
      {
        id: 'profit_loss_based_on_pat',
        name: 'Profit Loss Based on PAT',
        description: 'Check if profit/loss is based on Profit After Tax',
        category: 'Audit Report Compliance',
        documents: ['Audit Report']
      },
      {
        id: 'audit_firm_udin',
        name: 'Audit Firm UDIN',
        description: 'Verify UDIN number is mentioned for the audit firm',
        category: 'Audit Report Compliance',
        documents: ['Audit Report']
      },
      {
        id: 'consolidated_standalone_results',
        name: 'Consolidated Standalone Results',
        description: 'Check if consolidated results are present, standalone should be added before',
        category: 'Audit Report Compliance',
        documents: ['Audit Report']
      },
      {
        id: 'note_references_agreement',
        name: 'Note References Agreement',
        description: 'Verify note references in audit report match with financial statements',
        category: 'Audit Report Compliance',
        documents: ['Audit Report', 'Financial Statements']
      },
      {
        id: 'auditor_review_note',
        name: 'Auditor Review Note',
        description: 'Check for auditor review note in quarterly results',
        category: 'Audit Report Compliance',
        documents: ['Audit Report']
      },
      {
        id: 'audit_report_conclusion_opinion',
        name: 'Audit Report Conclusion Opinion',
        description: 'Verify audit report contains proper conclusion and opinion',
        category: 'Audit Report Compliance',
        documents: ['Audit Report']
      },
      {
        id: 'standard_audit_report_format',
        name: 'Standard Audit Report Format',
        description: 'Check if audit report follows standard format as per auditing standards',
        category: 'Audit Report Compliance',
        documents: ['Audit Report']
      },
      {
        id: 'company_name_consistency',
        name: 'Company Name Consistency',
        description: 'Verify company name is consistent throughout the report',
        category: 'Audit Report Compliance',
        documents: ['Audit Report']
      }
    ],
    financialStatementChecks: [
      {
        id: 'serial_numbers_sequence',
        name: 'Serial Numbers Sequence',
        description: 'Check if serial numbers are in proper sequence',
        category: 'Financial Statement Checks',
        documents: ['Financial Statements']
      },
      {
        id: 'group_definition_usage',
        name: 'Group Definition Usage',
        description: 'Verify proper usage of group definition in consolidated statements',
        category: 'Financial Statement Checks',
        documents: ['Financial Statements']
      },
      {
        id: 'company_word_highlighting',
        name: 'Company Word Highlighting',
        description: 'Check if company name is properly highlighted in the report',
        category: 'Financial Statement Checks',
        documents: ['Financial Statements']
      },
      {
        id: 'eps_pat_ratio_comparison',
        name: 'EPS PAT Ratio Comparison',
        description: 'Verify EPS and PAT ratios are calculated and compared correctly',
        category: 'Financial Statement Checks',
        documents: ['Financial Statements']
      },
      {
        id: 'fv_share_capital_consistency',
        name: 'Face Value Share Capital Consistency',
        description: 'Check face value and share capital figures are consistent',
        category: 'Financial Statement Checks',
        documents: ['Financial Statements']
      },
      {
        id: 'associates_jv_figures_tallying',
        name: 'Associates JV Figures Tallying',
        description: 'Verify associates and joint venture figures tally correctly',
        category: 'Financial Statement Checks',
        documents: ['Financial Statements']
      },
      {
        id: 'associates_jv_oci',
        name: 'Associates JV OCI',
        description: 'Check Other Comprehensive Income for associates and JVs',
        category: 'Financial Statement Checks',
        documents: ['Financial Statements']
      },
      {
        id: 'associates_jv_investment_tallying',
        name: 'Associates JV Investment Tallying',
        description: 'Verify investment figures in associates and JVs tally',
        category: 'Financial Statement Checks',
        documents: ['Financial Statements']
      },
      {
        id: 'other_equity_comparison',
        name: 'Other Equity Comparison',
        description: 'Check other equity items are properly compared and disclosed',
        category: 'Financial Statement Checks',
        documents: ['Financial Statements']
      },
      {
        id: 'cashflow_pbt_depreciation_tallying',
        name: 'Cash Flow PBT Depreciation Tallying',
        description: 'Verify cash flow PBT and depreciation figures tally',
        category: 'Financial Statement Checks',
        documents: ['Cash Flow Statement']
      }
    ],
    segmentReporting: [
      {
        id: 'segment_results_assets',
        name: 'Segment Results Assets',
        description: 'Check segment-wise assets are properly disclosed',
        category: 'Segment Reporting',
        documents: ['Financial Statements']
      },
      {
        id: 'segment_results_pnl_tallying',
        name: 'Segment Results P&L Tallying',
        description: 'Verify segment-wise P&L figures tally correctly',
        category: 'Segment Reporting',
        documents: ['Financial Statements']
      },
      {
        id: 'segment_depreciation_interest_tallying',
        name: 'Segment Depreciation Interest Tallying',
        description: 'Check segment-wise depreciation and interest figures tally',
        category: 'Segment Reporting',
        documents: ['Financial Statements']
      },
      {
        id: 'segment_net_assets_tallying',
        name: 'Segment Net Assets Tallying',
        description: 'Verify segment-wise net assets figures tally',
        category: 'Segment Reporting',
        documents: ['Financial Statements']
      },
      {
        id: 'segment_explanations',
        name: 'Segment Explanations',
        description: 'Check proper explanations are provided for segment reporting',
        category: 'Segment Reporting',
        documents: ['Financial Statements']
      },
      {
        id: 'segment_figures_bs_tallying',
        name: 'Segment Figures BS Tallying',
        description: 'Verify segment figures in balance sheet tally',
        category: 'Segment Reporting',
        documents: ['Balance Sheet']
      },
      {
        id: 'segment_results_explanation_note',
        name: 'Segment Results Explanation Note',
        description: 'Check explanatory notes for segment results are adequate',
        category: 'Segment Reporting',
        documents: ['Notes to Accounts']
      }
    ],
    regulatoryCompliance: [
      {
        id: 'foreign_subsidiaries_exchange_disclosure',
        name: 'Foreign Subsidiaries Exchange Disclosure',
        description: 'Check proper disclosure of foreign subsidiaries and exchange rates',
        category: 'Regulatory Compliance',
        documents: ['Financial Statements']
      },
      {
        id: 'group_components_note',
        name: 'Group Components Note',
        description: 'Verify group components are properly disclosed in notes',
        category: 'Regulatory Compliance',
        documents: ['Notes to Accounts']
      },
      {
        id: 'standalone_consolidated_notes_consistency',
        name: 'Standalone Consolidated Notes Consistency',
        description: 'Check consistency between standalone and consolidated notes',
        category: 'Regulatory Compliance',
        documents: ['Financial Statements']
      },
      {
        id: 'standard_review_audit_report_format',
        name: 'Standard Review Audit Report Format',
        description: 'Verify review audit report follows standard format',
        category: 'Regulatory Compliance',
        documents: ['Audit Report']
      },
      {
        id: 'profit_loss_quarter_ytd_consistency',
        name: 'Profit Loss Quarter YTD Consistency',
        description: 'Check consistency between quarterly and YTD profit/loss figures',
        category: 'Regulatory Compliance',
        documents: ['Financial Statements']
      },
      {
        id: 'foreign_components_translation_disclosure',
        name: 'Foreign Components Translation Disclosure',
        description: 'Verify disclosure of foreign currency translation',
        category: 'Regulatory Compliance',
        documents: ['Financial Statements']
      },
      {
        id: 'associates_jv_audit_report_mention',
        name: 'Associates JV Audit Report Mention',
        description: 'Check if associates and JVs are mentioned in audit report',
        category: 'Regulatory Compliance',
        documents: ['Audit Report']
      },
      {
        id: 'quarterly_ytd_figures_notes',
        name: 'Quarterly YTD Figures Notes',
        description: 'Verify quarterly and YTD figures are properly noted',
        category: 'Regulatory Compliance',
        documents: ['Financial Statements']
      },
      {
        id: 'other_auditors_mention',
        name: 'Other Auditors Mention',
        description: 'Check if other auditors are mentioned where applicable',
        category: 'Regulatory Compliance',
        documents: ['Audit Report']
      },
      {
        id: 'pre_consolidation_forex_figures',
        name: 'Pre Consolidation Forex Figures',
        description: 'Verify pre-consolidation forex figures are disclosed',
        category: 'Regulatory Compliance',
        documents: ['Financial Statements']
      }
    ],
    advancedChecks: [
      {
        id: 'subsidiary_word_highlighting',
        name: 'Subsidiary Word Highlighting',
        description: 'Check if subsidiary is properly highlighted in the report',
        category: 'Advanced Checks',
        documents: ['Financial Statements']
      },
      {
        id: 'sebi_lodr_reg_33_8_para',
        name: 'SEBI LODR Reg 33(8) Para',
        description: 'Verify compliance with SEBI LODR Regulation 33(8)',
        category: 'Advanced Checks',
        documents: ['Financial Statements']
      },
      {
        id: 'first_year_firm_previous_auditor',
        name: 'First Year Firm Previous Auditor',
        description: 'Check disclosure of previous auditor in first year of new firm',
        category: 'Advanced Checks',
        documents: ['Audit Report']
      },
      {
        id: 'goodwill_impairment_note',
        name: 'Goodwill Impairment Note',
        description: 'Verify goodwill impairment is properly disclosed',
        category: 'Advanced Checks',
        documents: ['Financial Statements']
      },
      {
        id: 'cross_references_correctness',
        name: 'Cross References Correctness',
        description: 'Check cross-references between different sections are correct',
        category: 'Advanced Checks',
        documents: ['Financial Statements']
      },
      {
        id: 'qualification_eom_consistency',
        name: 'Qualification EOM Consistency',
        description: 'Verify consistency between qualifications and emphasis of matter',
        category: 'Advanced Checks',
        documents: ['Audit Report']
      },
      {
        id: 'tax_ratio_deviation_highlighting',
        name: 'Tax Ratio Deviation Highlighting',
        description: 'Check if tax ratio deviations are properly highlighted',
        category: 'Advanced Checks',
        documents: ['Financial Statements']
      },
      {
        id: 'cin_number_mention',
        name: 'CIN Number Mention',
        description: 'Verify CIN number is mentioned in the report',
        category: 'Advanced Checks',
        documents: ['Financial Statements']
      },
      {
        id: 'firm_reg_number_correctness',
        name: 'Firm Reg Number Correctness',
        description: 'Check audit firm registration number is correct',
        category: 'Advanced Checks',
        documents: ['Audit Report']
      },
      {
        id: 'partner_name_mention',
        name: 'Partner Name Mention',
        description: 'Verify partner name is mentioned in audit report',
        category: 'Advanced Checks',
        documents: ['Audit Report']
      }
    ],
    finalChecks: [
      {
        id: 'partner_membership_number',
        name: 'Partner Membership Number',
        description: 'Check partner membership number is mentioned',
        category: 'Final Checks',
        documents: ['Audit Report']
      },
      {
        id: 'udin_number_mention',
        name: 'UDIN Number Mention',
        description: 'Verify UDIN number is properly mentioned',
        category: 'Final Checks',
        documents: ['Audit Report']
      },
      {
        id: 'date_place_consistency',
        name: 'Date Place Consistency',
        description: 'Check date and place are consistent throughout the report',
        category: 'Final Checks',
        documents: ['Audit Report']
      },
      {
        id: 'firm_seal_affixed',
        name: 'Firm Seal Affixed',
        description: 'Verify firm seal is affixed on the audit report',
        category: 'Final Checks',
        documents: ['Audit Report']
      },
      {
        id: 'audit_report_date_consistency',
        name: 'Audit Report Date Consistency',
        description: 'Check audit report date is consistent',
        category: 'Final Checks',
        documents: ['Audit Report']
      },
      {
        id: 'period_ended_as_at_phrases',
        name: 'Period Ended As At Phrases',
        description: 'Verify proper usage of period ended and as at phrases',
        category: 'Final Checks',
        documents: ['Financial Statements']
      },
      {
        id: 'segment_period_year_phrases',
        name: 'Segment Period Year Phrases',
        description: 'Check proper usage of period and year phrases in segment reporting',
        category: 'Final Checks',
        documents: ['Financial Statements']
      },
      {
        id: 'notes_figures_denomination',
        name: 'Notes Figures Denomination',
        description: 'Verify figures in notes are in proper denomination',
        category: 'Final Checks',
        documents: ['Notes to Accounts']
      },
      {
        id: 'tax_pbt_ratio_highlighting',
        name: 'Tax PBT Ratio Highlighting',
        description: 'Check if tax to PBT ratio is properly highlighted',
        category: 'Final Checks',
        documents: ['Financial Statements']
      }
    ]
  };

  // =====================================
  // INTERLINKED DOCUMENT CHECKS
  // =====================================
  const interlinkedDocumentChecks = {
    crossReferenceCompliance: [
      {
        id: 'ar_caro_reference_matching',
        name: 'Audit Report + CARO Reference Number',
        description: 'Verifying paragraph numbers match between Audit Report and CARO',
        category: 'Cross-Reference Compliance',
        documents: ['Audit Report', 'CARO Annexure A']
      },
      {
        id: 'ar_ifc_reference_matching',
        name: 'Audit Report + IFC Reference Number',
        description: 'Verifying paragraph numbers match between Audit Report and IFC',
        category: 'Cross-Reference Compliance',
        documents: ['Audit Report', 'IFC Annexure B']
      }
    ],
    assetVerification: [
      {
        id: 'bs_caro_ppe_check',
        name: 'Balance Sheet + CARO PPE Check',
        description: 'If PPE > 0, verifying CARO subclauses (i)(a)(A) & (i)(b)',
        category: 'Asset Verification',
        documents: ['Balance Sheet', 'CARO Annexure A']
      },
      {
        id: 'bs_caro_intangible_check',
        name: 'Balance Sheet + CARO Intangible Assets Check',
        description: 'If intangible assets > 0, verifying CARO subclause (i)(a)(B)',
        category: 'Asset Verification',
        documents: ['Balance Sheet', 'CARO Annexure A']
      },
      {
        id: 'notes_caro_immovable_property',
        name: 'Notes + CARO Immovable Property',
        description: 'If notes show immovable property, verifying CARO clause (i)(c)',
        category: 'Asset Verification',
        documents: ['Notes to Accounts', 'CARO Annexure A']
      },
      {
        id: 'notes_caro_revaluation',
        name: 'Notes + CARO Revaluation',
        description: 'If notes show revaluation, verifying CARO clause (i)(d)',
        category: 'Asset Verification',
        documents: ['Notes to Accounts', 'CARO Annexure A']
      }
    ],
    liabilityVerification: [
      {
        id: 'bs_caro_secured_borrowings',
        name: 'Balance Sheet + CARO Secured Borrowings',
        description: 'If secured borrowings > ₹5cr, verifying CARO clause (ii)(b)',
        category: 'Liability Verification',
        documents: ['Balance Sheet', 'CARO Annexure A', 'Notes to Accounts']
      },
      {
        id: 'bs_caro_fixed_deposits',
        name: 'Balance Sheet + CARO Fixed Deposits',
        description: 'If fixed deposits appear in liabilities, verifying CARO clause (v)',
        category: 'Liability Verification',
        documents: ['Balance Sheet', 'CARO Annexure A']
      },
      {
        id: 'notes_caro_contingent_liabilities',
        name: 'Notes + CARO Contingent Liabilities',
        description: 'If notes show financial guarantees, verifying CARO clauses (iii)(a)(A)+(B)',
        category: 'Liability Verification',
        documents: ['Notes to Accounts', 'CARO Annexure A']
      }
    ],
    investmentVerification: [
      {
        id: 'notes_caro_new_investments',
        name: 'Notes + CARO New Investments/Loans',
        description: 'If notes show new investments/loans/guarantees, verifying CARO clause (iii)',
        category: 'Investment Verification',
        documents: ['Notes to Accounts', 'CARO Annexure A']
      },
      {
        id: 'notes_caro_aggregate_threshold',
        name: 'Notes + CARO Aggregate Threshold',
        description: 'If aggregate of new loans/investments/guarantees exceeds 60% of opening net-worth, verifying CARO clause (iv)',
        category: 'Investment Verification',
        documents: ['Notes to Accounts', 'Balance Sheet', 'CARO Annexure A']
      }
    ],
    relatedPartyVerification: [
      {
        id: 'notes_caro_rpt',
        name: 'Notes + CARO Related Party Transactions',
        description: 'If notes disclose related party transactions, verifying CARO clause (iii)(f)',
        category: 'Related Party Verification',
        documents: ['Notes to Accounts', 'CARO Annexure A']
      }
    ],
    regulatoryCompliance: [
      {
        id: 'notes_caro_statutory_dues',
        name: 'Notes + CARO Statutory Dues',
        description: 'Verifying CARO clause (vii)(b) for any disputed statutory dues disclosure',
        category: 'Regulatory Compliance',
        documents: ['Notes to Accounts', 'CARO Annexure A']
      }
    ],
    goingConcernCompliance: [
      {
        id: 'audit_report_caro_material_uncertainty',
        name: 'Audit Report + CARO Material Uncertainty',
        description: 'Verifying if the audit report addresses material uncertainty related to going concern as per CARO requirements',
        category: 'Going Concern Compliance',
        documents: ['Audit Report', 'CARO Annexure A']
      }
    ],
    mugaCompliance: [
      {
        id: 'audit_muga_s143_ifc_inadequate',
        name: 'Audit Report + IFC Inadequate MUGA',
        description: 'Verifying if the audit report mentions inadequate MUGA as per Section 143(3)(i) and IFC requirements',
        category: 'MUGA Compliance',
        documents: ['Audit Report', 'IFC Annexure B']
      }
    ],
    liabilityDepositCompliance: [
      {
        id: 'notes_caro_adv_from_cust_deemed_deposit',
        name: 'Notes Advance from Customers + CARO (v) Deemed Deposit',
        description: 'If "Advance from Customers" in Notes changes year-on-year, CARO (v) should state no deemed deposit. If same, CARO (v) should indicate deemed deposit.',
        category: 'Liability & Deposit Compliance',
        documents: ['Notes to Accounts', 'CARO Annexure A'],
        conditions: ['Report Type = Normal/Standalone']
      }
    ],
    regulatoryComplianceAlignment: [
      {
        id: 'notes_caro_sebi_lodr_alignment',
        name: 'Notes SEBI LODR + CARO (iii)(a)(A) Alignment',
        description: 'Verifies if points from CARO clause (iii)(a)(A) are covered in the SEBI LODR note in Notes to Accounts. N/A if SEBI LODR note not found.',
        category: 'Regulatory Compliance Alignment',
        documents: ['Notes to Accounts', 'CARO Annexure A'],
        conditions: ['Report Type = Normal/Standalone']
      }
    ]
  };

  // =====================================
  // CONDITIONAL CHECKS
  // =====================================
  const conditionalChecks = {
    consolidatedCompliance: [
      {
        id: 'clause_21',
        name: 'Clause 21',
        description: 'Verifying Clause 21 in the Annexure A( CARO )',
        category: 'CARO Consolidated',
        documents: ['CARO Annexure A'],
        conditions: ['Report Type = Consolidated']
      },
      {
        id: 'consolidated_wording_consistency',
        name: 'Consolidated Statements Wording',
        description: 'Verifying consistent use of "consolidated" throughout documents',
        category: 'Consolidated Compliance',
        documents: ['Audit Report'],
        conditions: ['Report Type = Consolidated']
      }
    ],
    standaloneCompliance: [
      {
        id: 'clause_20',
        name: 'CARO Clause (xx)',
        description: 'Checking if consolidated CARO annexure contains exactly one heading numbered "(xx)"',
        category: 'CARO Standalone',
        documents: ['CARO Annexure A'],
        conditions: ['Report Type = Standalone/Normal']
      }
    ],
    nbfcCompliance: [
      {
        id: 'nbfc_caro_exemptions',
        name: 'NBFC CARO Exemptions',
        description: 'For NBFCs, checking if clauses (iii)(a) & (iii)(e) are correctly omitted',
        category: 'NBFC Compliance',
        documents: ['CARO Annexure A'],
        conditions: ['Is NBFC = Yes', 'Report Type ≠ Consolidated']
      }
    ]
  };

  // =====================================
  // MULTI-DOCUMENT CHECKS
  // =====================================
  const multiDocumentChecks = {
    crossReference: [
      {
        id: 'audit_report_annexure_a_reference',
        name: 'Audit Report + Annexure A Cross-Reference',
        description: 'Verifies matching paragraph numbers between Audit Report and Annexure A',
        category: 'Cross-Reference Compliance',
        documents: ['Audit Report', 'CARO Annexure A'],

      },
      {
        id: 'audit_report_annexure_b_reference',
        name: 'Audit Report + Annexure B Cross-Reference',
        description: 'Verifies matching paragraph numbers between Audit Report and Annexure B',
        category: 'Cross-Reference Compliance',
        documents: ['Audit Report', 'IFC Annexure B'],

      }
    ],
    balanceSheetCaro: [
      {
        id: 'bs_caro_intangible_assets',
        name: 'Balance Sheet + CARO Intangible Assets',
        description: 'Cross-verifies intangible assets cost with CARO clause (i)(a)(B) presence',
        category: 'Asset Disclosure Alignment',
        documents: ['Balance Sheet', 'CARO Annexure A'],

      },
      {
        id: 'bs_caro_ppe_assets',
        name: 'Balance Sheet + CARO PPE Assets',
        description: 'Aligns PPE assets cost with CARO clauses (i)(a)(A) and (i)(b)',
        category: 'Asset Disclosure Alignment',
        documents: ['Balance Sheet', 'CARO Annexure A'],

      },
      {
        id: 'bs_caro_inventory_assets',
        name: 'Balance Sheet + CARO Inventory',
        description: 'Cross-checks inventory value with CARO clause (ii)(a) verification',
        category: 'Asset Disclosure Alignment',
        documents: ['Balance Sheet', 'CARO Annexure A'],

      },
      {
        id: 'bs_caro_current_ratio_assets',
        name: 'Balance Sheet + CARO Current Ratio Analysis',
        description: 'Verifies current assets vs liabilities alignment with CARO clause (ix)(d)',
        category: 'Liquidity & Fund Utilization Alignment',
        documents: ['Balance Sheet', 'CARO Annexure A'],

      }
    ],
    notesCaroChecks: [
      {
        id: 'notes_caro_new_investments_loans',
        name: 'Notes New Investments/Loans + CARO Clause (iii)',
        description: 'Cross-verifies new investments and loans in Notes with CARO clause (iii) coverage',
        category: 'New Activities Compliance',
        documents: ['Notes to Accounts', 'CARO Annexure A'],

      },
      {
        id: 'notes_caro_doubtful_loans',
        name: 'Notes Doubtful Loans + CARO Clause (iii)(b)',
        description: 'Aligns provision for doubtful loans with CARO clause (iii)(b) recovery assessment',
        category: 'Doubtful Loans Compliance',
        documents: ['Notes to Accounts', 'CARO Annexure A'],

      },
      {
        id: 'notes_caro_aggregate_capital',
        name: 'Notes Aggregate Activities vs Capital + CARO Clause (iv)',
        description: 'Checks if aggregate activities exceed 60% of capital and verifies CARO clause (iv)',
        category: 'Capital Adequacy Compliance',
        documents: ['Notes to Accounts', 'CARO Annexure A'],

      },
      {
        id: 'notes_caro_statutory_dues_static',
        name: 'Notes Statutory Dues Comparison + CARO Clause (vii)(a)',
        description: 'Compares statutory dues between years and verifies CARO clause (vii)(a) disclosure',
        category: 'Statutory Dues Compliance',
        documents: ['Notes to Accounts', 'CARO Annexure A'],

      },
      {
        id: 'notes_caro_capital_debt_increase',
        name: 'Notes Capital/Debt Increase + CARO Clause (x)(a)',
        description: 'Verifies share capital or debt increase with CARO clause (x)(a) disclosure',
        category: 'Capital Raising Compliance',
        documents: ['Notes to Accounts', 'CARO Annexure A'],

      },
      {
        id: 'notes_caro_rpt_minority_approval',
        name: 'Notes RPT >10% Turnover + CARO Clause (xiii)',
        description: 'Checks if RPT exceeds 10% of turnover and verifies CARO clause (xiii) minority approval',
        category: 'Related Party Transactions Compliance',
        documents: ['Notes to Accounts', 'CARO Annexure A'],

      },
      {
        id: 'notes_caro_related_party_loans_alignment',
        name: 'Notes + CARO Related Party Loans Alignment',
        description: 'Cross-verifies related party loan amounts between Notes and CARO clauses (iii)(a)(A) and (iii)(f)',
        category: 'Related Party Loans Alignment',
        documents: ['Notes to Accounts', 'CARO Annexure A'],

      }
    ],
    comprehensiveChecks: [
      {
        id: 'secretarial_caro_comprehensive',
        name: 'Comprehensive Secretarial Audit + CARO Alignment',
        description: 'Cross-verifies all key regulatory sections between Secretarial Audit and CARO clauses',
        category: 'Comprehensive Regulatory Cross-Compliance',
        documents: ['Secretarial Report', 'CARO Annexure A'],

      },
      {
        id: 'enhanced_bs_notes_caro_fixed_deposits',
        name: 'Enhanced Fixed Deposits Comprehensive Analysis',
        description: 'Extracts note numbers from Balance Sheet, analyzes Notes for fixed deposits, verifies CARO clause (v)',
        category: 'Enhanced Fixed Deposits Compliance',
        documents: ['Balance Sheet', 'Notes to Accounts', 'CARO Annexure A'],

      },
      // REMOVED: inventory_goods_in_transit_check - Now handled by enhanced multi-document processor
      {
        id: 'inventory_writeoff_check',
        name: 'Inventory Write-off Verification',
        description: 'Checks for inventory write-offs in Notes and verifies CARO clause (ii)(a) discrepancy disclosure',
        category: 'Inventory Write-off Compliance',
        documents: ['Balance Sheet', 'Notes to Accounts', 'CARO Annexure A'],

      },
      {
        id: 'secured_borrowings_quarterly_returns_check',
        name: 'Secured Borrowings Quarterly Returns',
        description: 'Checks secured borrowings >5 crores and verifies CARO clause (ii)(b) quarterly returns disclosure',
        category: 'Secured Borrowings Quarterly Returns Compliance',
        documents: ['Balance Sheet', 'Notes to Accounts', 'CARO Annexure A'],

      }
    ],
    annualReportCaro: [
      {
        id: 'annual_report_caro_income_tax',
        name: 'Annual Report Income Tax + CARO Clause (viii)',
        description: 'Searches for income tax raids keywords in Annual Report and verifies CARO clause (viii)',
        category: 'Annual Report Cross-Compliance',
        documents: ['Annual Report', 'CARO Annexure A'],

      },
      {
        id: 'annual_report_caro_defaults',
        name: 'Annual Report Defaults + CARO Clause (ix)',
        description: 'Searches for defaults/wilful defaulter keywords and verifies CARO clause (ix)',
        category: 'Annual Report Cross-Compliance',
        documents: ['Annual Report', 'CARO Annexure A'],

      },
      {
        id: 'annual_report_caro_rights_issue',
        name: 'Annual Report Rights Issue + CARO Clause (x)(b)',
        description: 'Searches for rights issue keywords and verifies CARO clause (x)(b)',
        category: 'Annual Report Cross-Compliance',
        documents: ['Annual Report', 'CARO Annexure A'],

      },
      {
        id: 'annual_report_caro_fraud',
        name: 'Annual Report Fraud + CARO Clause (xi)(a)',
        description: 'Searches for fraud keywords and verifies CARO clause (xi)(a)',
        category: 'Annual Report Cross-Compliance',
        documents: ['Annual Report', 'CARO Annexure A'],

      },
      {
        id: 'annual_report_caro_whistleblower',
        name: 'Annual Report Whistle-blower + CARO Clause (xi)(c)',
        description: 'Searches for whistle-blower keywords and verifies CARO clause (xi)(c)',
        category: 'Annual Report Cross-Compliance',
        documents: ['Annual Report', 'CARO Annexure A'],

      },
      {
        id: 'annual_report_caro_cost_records',
        name: 'Annual Report Cost Records + CARO Clause (vi)',
        description: 'Searches for cost records/auditor keywords and verifies CARO clause (vi)',
        category: 'Annual Report Cross-Compliance',
        documents: ['Annual Report', 'CARO Annexure A'],

      }
    ]
  };

  // Calculate totals
  const totalIndependentChecks = Object.values(independentDocumentChecks).flat().length;
  const totalInterlinkedChecks = Object.values(interlinkedDocumentChecks).flat().length;
  const totalConditionalChecks = Object.values(conditionalChecks).flat().length;
  const totalSingleDocChecks = singleDocumentChecks.auditReport.length + singleDocumentChecks.caroAnnexure.length;
  const totalResultsAuditChecks = Object.values(resultsAuditChecks).flat().length;
  const totalMultiDocChecks = Object.values(multiDocumentChecks).flat().length;
  const totalChecks = totalIndependentChecks + totalInterlinkedChecks + totalConditionalChecks + totalSingleDocChecks + totalResultsAuditChecks + totalMultiDocChecks;

  // Render individual check item
  const renderCheckItem = (check: any, showDetails = false) => (
    <li key={check.id} className="checklist-item border rounded-lg p-4 mb-3 hover:bg-gray-50 transition-colors">
      <div className="flex items-start">
        <CheckCircle className="h-5 w-5 text-green-500 mt-1 mr-3 flex-shrink-0" />
        <div className="flex-1">
          <div className="font-medium text-gray-900 mb-1">
            {check.name}
          </div>
          <div className="text-sm text-gray-600 mb-2">
            {check.description}
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-2">
            <Badge variant="outline" className="text-xs">
              {check.category}
            </Badge>
          </div>

          <div className="text-xs text-gray-500">
            <span className="font-medium">Documents:</span> {check.documents.join(", ")}
            {check.conditions && check.conditions.length > 0 && (
              <span className="ml-3">
                <span className="font-medium">Conditions:</span> {check.conditions.join(", ")}
              </span>
            )}
          </div>

          {showDetails && (
            <div className="mt-2 text-xs text-blue-600">
              <span className="font-medium">Processing:</span> AI-powered analysis with intelligent routing
            </div>
          )}
        </div>
      </div>
    </li>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <DashboardTopbar />
      <div className="pt-20 px-4 sm:px-6 lg:px-8">
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Audit Compliance Checklists</h1>
              <p className="text-gray-600 mt-1">Comprehensive AI-powered audit compliance verification system</p>
            </div>

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowAllChecks(!showAllChecks)}>
                {showAllChecks ? "Show Summary" : "Show All Details"}
              </Button>
              <Button>
                <DownloadCloud className="mr-2 h-4 w-4" />
                Export Checklist
              </Button>
            </div>
          </div>

      <div className="bg-white rounded-lg shadow-sm border">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="overflow-x-auto">
            <TabsList className="w-max min-w-full justify-start p-1 h-auto bg-gray-50 rounded-t-lg">
              <TabsTrigger value="overview" className="flex items-center gap-2 whitespace-nowrap">
                <InfoIcon className="h-4 w-4" />
                Overview
              </TabsTrigger>
              <TabsTrigger value="independent" className="flex items-center gap-2 whitespace-nowrap">
                <FileText className="h-4 w-4" />
                Independent ({totalIndependentChecks})
              </TabsTrigger>
              <TabsTrigger value="interlinked" className="flex items-center gap-2 whitespace-nowrap">
                <ExternalLink className="h-4 w-4" />
                Interlinked ({totalInterlinkedChecks})
              </TabsTrigger>
              <TabsTrigger value="conditional" className="flex items-center gap-2 whitespace-nowrap">
                <AlertTriangle className="h-4 w-4" />
                Conditional ({totalConditionalChecks})
              </TabsTrigger>
              <TabsTrigger value="results-audit" className="flex items-center gap-2 whitespace-nowrap">
                <TrendingUp className="h-4 w-4" />
                Results Audit ({totalResultsAuditChecks})
              </TabsTrigger>
              <TabsTrigger value="multi-document" className="flex items-center gap-2 whitespace-nowrap">
                <BadgeCheck className="h-4 w-4" />
                Multi Document ({totalMultiDocChecks})
              </TabsTrigger>
              <TabsTrigger value="cross-compliance" className="flex items-center gap-2 whitespace-nowrap">
                <Search className="h-4 w-4" />
                Annual Report Cross-Compliance (6)
              </TabsTrigger>
              <TabsTrigger value="ai-routing" className="flex items-center gap-2 whitespace-nowrap">
                <Shield className="h-4 w-4" />
                AI Model Routing
              </TabsTrigger>
            </TabsList>
          </div>

          <div className="p-6">
            <TabsContent value="overview" className="space-y-6">
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg border border-blue-200">
                <div className="flex items-center mb-3">
                  <InfoIcon className="h-6 w-6 text-blue-600 mr-3" />
                  <h3 className="text-lg font-semibold text-blue-900">System Overview</h3>
                </div>
                <p className="text-blue-800 mb-4">
                  Our comprehensive audit compliance system performs {totalChecks} different verification points using advanced AI models (Gemini and OpenAI)
                  with intelligent routing based on document type and complexity. The system dynamically selects appropriate checks based
                  on your uploaded documents and company parameters.
                </p>
                <div className="bg-white p-4 rounded-md border border-blue-200">
                  <h4 className="font-medium text-blue-900 mb-2">Key Features:</h4>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>• AI-powered document analysis with model routing optimization</li>
                    <li>• Conditional logic based on company type, listing status, and audit parameters</li>
                    <li>• Cross-document verification ensuring consistency across financial statements</li>
                    <li>• Real-time text search combined with advanced AI analysis</li>
                    <li>• Comprehensive regulatory compliance coverage (CARO, Companies Act, Audit Standards)</li>
                  </ul>
                </div>
              </div>

              {/* Statistics Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
                <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-2xl font-bold text-blue-700">{totalIndependentChecks}</div>
                    <FileText className="h-8 w-8 text-blue-600" />
                  </div>
                  <div className="text-sm text-blue-700 font-medium mb-1">Independent Checks</div>
                  <div className="text-xs text-blue-600">Single document verification</div>
                </div>

                <div className="bg-cyan-50 p-6 rounded-lg border border-cyan-200">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-2xl font-bold text-cyan-700">{totalInterlinkedChecks}</div>
                    <ExternalLink className="h-8 w-8 text-cyan-600" />
                  </div>
                  <div className="text-sm text-cyan-700 font-medium mb-1">Interlinked Checks</div>
                  <div className="text-xs text-cyan-600">Two-document cross-verification</div>
                </div>

                <div className="bg-yellow-50 p-6 rounded-lg border border-yellow-200">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-2xl font-bold text-yellow-700">{totalConditionalChecks}</div>
                    <AlertTriangle className="h-8 w-8 text-yellow-600" />
                  </div>
                  <div className="text-sm text-yellow-700 font-medium mb-1">Conditional Checks</div>
                  <div className="text-xs text-yellow-600">Parameter-based verification</div>
                </div>

                <div className="bg-indigo-50 p-6 rounded-lg border border-indigo-200">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-2xl font-bold text-indigo-700">{totalResultsAuditChecks}</div>
                    <TrendingUp className="h-8 w-8 text-indigo-600" />
                  </div>
                  <div className="text-sm text-indigo-700 font-medium mb-1">Results Audit Checks</div>
                  <div className="text-xs text-indigo-600">Quarterly results compliance</div>
                </div>

                <div className="bg-green-50 p-6 rounded-lg border border-green-200">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-2xl font-bold text-green-700">{totalMultiDocChecks - 6}</div>
                    <BadgeCheck className="h-8 w-8 text-green-600" />
                  </div>
                  <div className="text-sm text-green-700 font-medium mb-1">Multi-Document Checks</div>
                  <div className="text-xs text-green-600">Complex cross-document verification</div>
                </div>

                <div className="bg-orange-50 p-6 rounded-lg border border-orange-200">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-2xl font-bold text-orange-700">{totalChecks}</div>
                    <Shield className="h-8 w-8 text-orange-600" />
                  </div>
                  <div className="text-sm text-orange-700 font-medium mb-1">Total Checks</div>
                  <div className="text-xs text-orange-600">Complete compliance verification coverage</div>
                </div>
              </div>

              {/* Recent Completions */}
              <div className="bg-white rounded-lg border p-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                  System Capabilities Highlights
                </h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-medium text-gray-900 mb-3">Advanced AI Processing</h4>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li>• Intelligent model routing (Gemini/OpenAI) based on document complexity</li>
                      <li>• Multi-step conditional logic for parameter-based check activation</li>
                      <li>• Real-time text search with AI-powered validation</li>
                      <li>• Cross-document amount alignment with tolerance calculations</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900 mb-3">Regulatory Coverage</h4>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li>• Complete CARO 2020 compliance (all 20 clauses for standalone, clause xxi for consolidated)</li>
                      <li>• Companies Act sections 177, 188, 185, 186, 192, 135, 197(16), 148</li>
                      <li>• NBFC-specific exemptions and requirements</li>
                      <li>• Listed company BRSR/BRR compliance based on Top 1000/500 status</li>
                    </ul>
                  </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="independent" className="space-y-6">
              <div className="space-y-6">
                <Accordion type="multiple" className="w-full">
                  <AccordionItem value="basic-compliance">
                    <AccordionTrigger className="text-lg font-semibold">
                      <span className="font-semibold">Basic Compliance ({independentDocumentChecks.basicCompliance.length})</span>
                    </AccordionTrigger>
                    <AccordionContent>
                      <ul className="space-y-3">
                        {independentDocumentChecks.basicCompliance.map((check) => renderCheckItem(check, showAllChecks))}
                      </ul>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="statement-type-compliance">
                    <AccordionTrigger className="text-lg font-semibold">
                      <span className="font-semibold">Statement Type Compliance ({independentDocumentChecks.statementTypeCompliance.length})</span>
                    </AccordionTrigger>
                    <AccordionContent>
                      <ul className="space-y-3">
                        {independentDocumentChecks.statementTypeCompliance.map((check) => renderCheckItem(check, showAllChecks))}
                      </ul>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="audit-standards">
                    <AccordionTrigger className="text-lg font-semibold">
                      <span className="font-semibold">Audit Standards ({independentDocumentChecks.auditStandards.length})</span>
                    </AccordionTrigger>
                    <AccordionContent>
                      <ul className="space-y-3">
                        {independentDocumentChecks.auditStandards.map((check) => renderCheckItem(check, showAllChecks))}
                      </ul>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="regulatory-compliance">
                    <AccordionTrigger className="text-lg font-semibold">
                      <span className="font-semibold">Regulatory Compliance ({independentDocumentChecks.regulatoryCompliance.length})</span>
                    </AccordionTrigger>
                    <AccordionContent>
                      <ul className="space-y-3">
                        {independentDocumentChecks.regulatoryCompliance.map((check) => renderCheckItem(check, showAllChecks))}
                      </ul>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="caro-compliance">
                    <AccordionTrigger className="text-lg font-semibold">
                      <span className="font-semibold">CARO Compliance ({independentDocumentChecks.caroCompliance.length})</span>
                    </AccordionTrigger>
                    <AccordionContent>
                      <ul className="space-y-3">
                        {independentDocumentChecks.caroCompliance.map((check) => renderCheckItem(check, showAllChecks))}
                      </ul>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
            </TabsContent>

            <TabsContent value="interlinked" className="space-y-6">
              <div className="space-y-6">
                <Accordion type="multiple" className="w-full">
                  <AccordionItem value="cross-reference-compliance">
                    <AccordionTrigger className="text-lg font-semibold">
                      <span className="font-semibold">Cross-Reference Compliance ({interlinkedDocumentChecks.crossReferenceCompliance.length})</span>
                    </AccordionTrigger>
                    <AccordionContent>
                      <ul className="space-y-3">
                        {interlinkedDocumentChecks.crossReferenceCompliance.map((check) => renderCheckItem(check, showAllChecks))}
                      </ul>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="asset-verification">
                    <AccordionTrigger className="text-lg font-semibold">
                      <span className="font-semibold">Asset Verification ({interlinkedDocumentChecks.assetVerification.length})</span>
                    </AccordionTrigger>
                    <AccordionContent>
                      <ul className="space-y-3">
                        {interlinkedDocumentChecks.assetVerification.map((check) => renderCheckItem(check, showAllChecks))}
                      </ul>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="liability-verification">
                    <AccordionTrigger className="text-lg font-semibold">
                      <span className="font-semibold">Liability Verification ({interlinkedDocumentChecks.liabilityVerification.length})</span>
                    </AccordionTrigger>
                    <AccordionContent>
                      <ul className="space-y-3">
                        {interlinkedDocumentChecks.liabilityVerification.map((check) => renderCheckItem(check, showAllChecks))}
                      </ul>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="investment-verification">
                    <AccordionTrigger className="text-lg font-semibold">
                      <span className="font-semibold">Investment Verification ({interlinkedDocumentChecks.investmentVerification.length})</span>
                    </AccordionTrigger>
                    <AccordionContent>
                      <ul className="space-y-3">
                        {interlinkedDocumentChecks.investmentVerification.map((check) => renderCheckItem(check, showAllChecks))}
                      </ul>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="related-party-verification">
                    <AccordionTrigger className="text-lg font-semibold">
                      <span className="font-semibold">Related Party Verification ({interlinkedDocumentChecks.relatedPartyVerification.length})</span>
                    </AccordionTrigger>
                    <AccordionContent>
                      <ul className="space-y-3">
                        {interlinkedDocumentChecks.relatedPartyVerification.map((check) => renderCheckItem(check, showAllChecks))}
                      </ul>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="regulatory-compliance">
                    <AccordionTrigger className="text-lg font-semibold">
                      <span className="font-semibold">Regulatory Compliance ({interlinkedDocumentChecks.regulatoryCompliance.length})</span>
                    </AccordionTrigger>
                    <AccordionContent>
                      <ul className="space-y-3">
                        {interlinkedDocumentChecks.regulatoryCompliance.map((check) => renderCheckItem(check, showAllChecks))}
                      </ul>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="going-concern-compliance">
                    <AccordionTrigger className="text-lg font-semibold">
                      <span className="font-semibold">Going Concern Compliance ({interlinkedDocumentChecks.goingConcernCompliance.length})</span>
                    </AccordionTrigger>
                    <AccordionContent>
                      <ul className="space-y-3">
                        {interlinkedDocumentChecks.goingConcernCompliance.map((check) => renderCheckItem(check, showAllChecks))}
                      </ul>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="muga-compliance">
                    <AccordionTrigger className="text-lg font-semibold">
                      <span className="font-semibold">MUGA Compliance ({interlinkedDocumentChecks.mugaCompliance.length})</span>
                    </AccordionTrigger>
                    <AccordionContent>
                      <ul className="space-y-3">
                        {interlinkedDocumentChecks.mugaCompliance.map((check) => renderCheckItem(check, showAllChecks))}
                      </ul>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="liability-deposit-compliance">
                    <AccordionTrigger className="text-lg font-semibold">
                      <span className="font-semibold">Liability & Deposit Compliance ({interlinkedDocumentChecks.liabilityDepositCompliance.length})</span>
                    </AccordionTrigger>
                    <AccordionContent>
                      <ul className="space-y-3">
                        {interlinkedDocumentChecks.liabilityDepositCompliance.map((check) => renderCheckItem(check, showAllChecks))}
                      </ul>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="regulatory-compliance-alignment">
                    <AccordionTrigger className="text-lg font-semibold">
                      <span className="font-semibold">Regulatory Compliance Alignment ({interlinkedDocumentChecks.regulatoryComplianceAlignment.length})</span>
                    </AccordionTrigger>
                    <AccordionContent>
                      <ul className="space-y-3">
                        {interlinkedDocumentChecks.regulatoryComplianceAlignment.map((check) => renderCheckItem(check, showAllChecks))}
                      </ul>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
            </TabsContent>

            <TabsContent value="conditional" className="space-y-6">
              <div className="space-y-6">
                <Accordion type="multiple" className="w-full">
                  <AccordionItem value="consolidated-compliance">
                    <AccordionTrigger className="text-lg font-semibold">
                      <span className="font-semibold">Consolidated Compliance ({conditionalChecks.consolidatedCompliance.length})</span>
                    </AccordionTrigger>
                    <AccordionContent>
                      <ul className="space-y-3">
                        {conditionalChecks.consolidatedCompliance.map((check) => renderCheckItem(check, showAllChecks))}
                      </ul>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="standalone-compliance">
                    <AccordionTrigger className="text-lg font-semibold">
                      <span className="font-semibold">Standalone Compliance ({conditionalChecks.standaloneCompliance.length})</span>
                    </AccordionTrigger>
                    <AccordionContent>
                      <ul className="space-y-3">
                        {conditionalChecks.standaloneCompliance.map((check) => renderCheckItem(check, showAllChecks))}
                      </ul>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="nbfc-compliance">
                    <AccordionTrigger className="text-lg font-semibold">
                      <span className="font-semibold">NBFC Compliance ({conditionalChecks.nbfcCompliance.length})</span>
                    </AccordionTrigger>
                    <AccordionContent>
                      <ul className="space-y-3">
                        {conditionalChecks.nbfcCompliance.map((check) => renderCheckItem(check, showAllChecks))}
                      </ul>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
            </TabsContent>

            <TabsContent value="single-document" className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold">Single Document Checks ({totalSingleDocChecks})</h2>
                <Badge variant="outline">AI-Powered Analysis</Badge>
              </div>

              <p className="text-gray-600">
                These checks analyze individual documents using advanced AI models with intelligent routing.
                Each check is processed by the most suitable AI model (Gemini or OpenAI) based on document type and complexity.
              </p>

              <Accordion type="single" collapsible className="w-full">
                <AccordionItem value="audit-report" className="border rounded-lg mb-4">
                  <AccordionTrigger className="px-4 py-3 hover:bg-gray-50 rounded-t-lg">
                    <div className="flex items-center justify-between w-full">
                      <div className="flex items-center">
                        <FileText className="h-5 w-5 mr-2 text-blue-600" />
                        <span className="font-semibold">Audit Report Checks ({singleDocumentChecks.auditReport.length})</span>
                      </div>
                      <Badge variant="secondary" className="text-xs">Gemini/OpenAI Routing</Badge>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 py-3">
                    <ul className="space-y-2">
                      {singleDocumentChecks.auditReport.map((check) => renderCheckItem(check, showAllChecks))}
                    </ul>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="caro-annexure" className="border rounded-lg mb-4">
                  <AccordionTrigger className="px-4 py-3 hover:bg-gray-50 rounded-t-lg">
                    <div className="flex items-center justify-between w-full">
                      <div className="flex items-center">
                        <Gavel className="h-5 w-5 mr-2 text-red-600" />
                        <span className="font-semibold">CARO Annexure Checks ({singleDocumentChecks.caroAnnexure.length})</span>
                      </div>
                      <Badge variant="secondary" className="text-xs">Advanced AI Analysis</Badge>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 py-3">
                    <div className="bg-yellow-50 p-3 rounded-md mb-4 border border-yellow-200">
                      <div className="text-sm text-yellow-800">
                        <strong>Conditional Logic:</strong> CARO checks are dynamically selected based on your audit report type:
                        <ul className="mt-1 ml-4 list-disc">
                          <li><strong>Standalone/Normal:</strong> Clauses (i) to (xx) + specialized checks</li>
                          <li><strong>Consolidated:</strong> Only clause (xxi)</li>
                          <li><strong>NBFC:</strong> Additional exemption validations</li>
                        </ul>
                      </div>
                    </div>
                    <ul className="space-y-2">
                      {singleDocumentChecks.caroAnnexure.map((check) => renderCheckItem(check, showAllChecks))}
                    </ul>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </TabsContent>

            <TabsContent value="results-audit" className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold">Results Audit Checks ({totalResultsAuditChecks})</h2>
                <Badge variant="outline">Quarterly Results Analysis</Badge>
              </div>

              <p className="text-gray-600">
                These checks analyze quarterly financial results and audit reports using advanced AI models.
                Each check is specifically designed for quarterly results compliance and disclosure verification.
              </p>

              <Accordion type="single" collapsible className="w-full">
                <AccordionItem value="basic-compliance" className="border rounded-lg mb-4">
                  <AccordionTrigger className="px-4 py-3 hover:bg-gray-50 rounded-t-lg">
                    <div className="flex items-center justify-between w-full">
                      <div className="flex items-center">
                        <CheckCircle className="h-5 w-5 mr-2 text-green-600" />
                        <span className="font-semibold">Basic Compliance ({resultsAuditChecks.basicCompliance.length})</span>
                      </div>
                      <Badge variant="secondary" className="text-xs">Essential Checks</Badge>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 py-3">
                    <ul className="space-y-2">
                      {resultsAuditChecks.basicCompliance.map((check) => renderCheckItem(check, showAllChecks))}
                    </ul>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="audit-report-compliance" className="border rounded-lg mb-4">
                  <AccordionTrigger className="px-4 py-3 hover:bg-gray-50 rounded-t-lg">
                    <div className="flex items-center justify-between w-full">
                      <div className="flex items-center">
                        <FileText className="h-5 w-5 mr-2 text-blue-600" />
                        <span className="font-semibold">Audit Report Compliance ({resultsAuditChecks.auditReportCompliance.length})</span>
                      </div>
                      <Badge variant="secondary" className="text-xs">Audit Standards</Badge>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 py-3">
                    <ul className="space-y-2">
                      {resultsAuditChecks.auditReportCompliance.map((check) => renderCheckItem(check, showAllChecks))}
                    </ul>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="financial-statement-checks" className="border rounded-lg mb-4">
                  <AccordionTrigger className="px-4 py-3 hover:bg-gray-50 rounded-t-lg">
                    <div className="flex items-center justify-between w-full">
                      <div className="flex items-center">
                        <Building className="h-5 w-5 mr-2 text-purple-600" />
                        <span className="font-semibold">Financial Statement Checks ({resultsAuditChecks.financialStatementChecks.length})</span>
                      </div>
                      <Badge variant="secondary" className="text-xs">Financial Analysis</Badge>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 py-3">
                    <ul className="space-y-2">
                      {resultsAuditChecks.financialStatementChecks.map((check) => renderCheckItem(check, showAllChecks))}
                    </ul>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="segment-reporting" className="border rounded-lg mb-4">
                  <AccordionTrigger className="px-4 py-3 hover:bg-gray-50 rounded-t-lg">
                    <div className="flex items-center justify-between w-full">
                      <div className="flex items-center">
                        <Users className="h-5 w-5 mr-2 text-indigo-600" />
                        <span className="font-semibold">Segment Reporting ({resultsAuditChecks.segmentReporting.length})</span>
                      </div>
                      <Badge variant="secondary" className="text-xs">Segment Analysis</Badge>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 py-3">
                    <ul className="space-y-2">
                      {resultsAuditChecks.segmentReporting.map((check) => renderCheckItem(check, showAllChecks))}
                    </ul>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="regulatory-compliance" className="border rounded-lg mb-4">
                  <AccordionTrigger className="px-4 py-3 hover:bg-gray-50 rounded-t-lg">
                    <div className="flex items-center justify-between w-full">
                      <div className="flex items-center">
                        <Gavel className="h-5 w-5 mr-2 text-red-600" />
                        <span className="font-semibold">Regulatory Compliance ({resultsAuditChecks.regulatoryCompliance.length})</span>
                      </div>
                      <Badge variant="secondary" className="text-xs">Regulatory Standards</Badge>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 py-3">
                    <ul className="space-y-2">
                      {resultsAuditChecks.regulatoryCompliance.map((check) => renderCheckItem(check, showAllChecks))}
                    </ul>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="advanced-checks" className="border rounded-lg mb-4">
                  <AccordionTrigger className="px-4 py-3 hover:bg-gray-50 rounded-t-lg">
                    <div className="flex items-center justify-between w-full">
                      <div className="flex items-center">
                        <Shield className="h-5 w-5 mr-2 text-orange-600" />
                        <span className="font-semibold">Advanced Checks ({resultsAuditChecks.advancedChecks.length})</span>
                      </div>
                      <Badge variant="secondary" className="text-xs">Advanced Analysis</Badge>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 py-3">
                    <ul className="space-y-2">
                      {resultsAuditChecks.advancedChecks.map((check) => renderCheckItem(check, showAllChecks))}
                    </ul>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="final-checks" className="border rounded-lg mb-4">
                  <AccordionTrigger className="px-4 py-3 hover:bg-gray-50 rounded-t-lg">
                    <div className="flex items-center justify-between w-full">
                      <div className="flex items-center">
                        <Clock className="h-5 w-5 mr-2 text-gray-600" />
                        <span className="font-semibold">Final Checks ({resultsAuditChecks.finalChecks.length})</span>
                      </div>
                      <Badge variant="secondary" className="text-xs">Final Verification</Badge>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 py-3">
                    <ul className="space-y-2">
                      {resultsAuditChecks.finalChecks.map((check) => renderCheckItem(check, showAllChecks))}
                    </ul>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </TabsContent>

            <TabsContent value="multi-document" className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold">Multi-Document Checks ({totalMultiDocChecks - 6})</h2>
                <Badge variant="outline">Cross-Document Verification</Badge>
              </div>

              <p className="text-gray-600">
                These checks compare and cross-verify information across multiple documents to ensure consistency,
                completeness, and regulatory compliance alignment.
              </p>

              <Accordion type="single" collapsible className="w-full">
                <AccordionItem value="cross-reference" className="border rounded-lg mb-4">
                  <AccordionTrigger className="px-4 py-3 hover:bg-gray-50 rounded-t-lg">
                    <div className="flex items-center justify-between w-full">
                      <div className="flex items-center">
                        <ExternalLink className="h-5 w-5 mr-2 text-green-600" />
                        <span className="font-semibold">Cross-Reference Compliance ({multiDocumentChecks.crossReference.length})</span>
                      </div>
                      <Badge variant="secondary" className="text-xs">Paragraph Matching</Badge>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 py-3">
                    <ul className="space-y-2">
                      {multiDocumentChecks.crossReference.map((check) => renderCheckItem(check, showAllChecks))}
                    </ul>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="balance-sheet-caro" className="border rounded-lg mb-4">
                  <AccordionTrigger className="px-4 py-3 hover:bg-gray-50 rounded-t-lg">
                    <div className="flex items-center justify-between w-full">
                      <div className="flex items-center">
                        <Building className="h-5 w-5 mr-2 text-blue-600" />
                        <span className="font-semibold">Balance Sheet + CARO Alignment ({multiDocumentChecks.balanceSheetCaro.length})</span>
                      </div>
                      <Badge variant="secondary" className="text-xs">Asset Verification</Badge>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 py-3">
                    <div className="bg-blue-50 p-3 rounded-md mb-4 border border-blue-200">
                      <div className="text-sm text-blue-800">
                        <strong>Logic:</strong> If Balance Sheet shows assets greater than 0, corresponding CARO clauses must be present.
                        Includes PPE, Intangible Assets, Inventory, and Current Ratio analysis.
                      </div>
                    </div>
                    <ul className="space-y-2">
                      {multiDocumentChecks.balanceSheetCaro.map((check) => renderCheckItem(check, showAllChecks))}
                    </ul>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="notes-caro" className="border rounded-lg mb-4">
                  <AccordionTrigger className="px-4 py-3 hover:bg-gray-50 rounded-t-lg">
                    <div className="flex items-center justify-between w-full">
                      <div className="flex items-center">
                        <Users className="h-5 w-5 mr-2 text-purple-600" />
                        <span className="font-semibold">Notes + CARO Compliance ({multiDocumentChecks.notesCaroChecks.length})</span>
                      </div>
                      <Badge variant="secondary" className="text-xs">Advanced Logic</Badge>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 py-3">
                    <div className="bg-purple-50 p-3 rounded-md mb-4 border border-purple-200">
                      <div className="text-sm text-purple-800">
                        <strong>Advanced Features:</strong> Amount alignment with 5% tolerance, RPT  greater than 10% turnover detection,
                        60% capital threshold calculations, and related party loan cross-verification.
                      </div>
                    </div>
                    <ul className="space-y-2">
                      {multiDocumentChecks.notesCaroChecks.map((check) => renderCheckItem(check, showAllChecks))}
                    </ul>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="comprehensive" className="border rounded-lg mb-4">
                  <AccordionTrigger className="px-4 py-3 hover:bg-gray-50 rounded-t-lg">
                    <div className="flex items-center justify-between w-full">
                      <div className="flex items-center">
                        <Shield className="h-5 w-5 mr-2 text-indigo-600" />
                        <span className="font-semibold">Comprehensive Multi-Document ({multiDocumentChecks.comprehensiveChecks.length})</span>
                      </div>
                      <Badge variant="secondary" className="text-xs">3-Document Analysis</Badge>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 py-3">
                    <div className="bg-indigo-50 p-3 rounded-md mb-4 border border-indigo-200">
                      <div className="text-sm text-indigo-800">
                        <strong>Enhanced Processing:</strong> These checks involve 3+ documents with dynamic note extraction,
                        variable substitution, and comprehensive secretarial audit alignment.
                      </div>
                    </div>
                    <ul className="space-y-2">
                      {multiDocumentChecks.comprehensiveChecks.map((check) => renderCheckItem(check, showAllChecks))}
                    </ul>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </TabsContent>

            <TabsContent value="cross-compliance" className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold">Annual Report + CARO Cross-Compliance (6)</h2>
                <Badge variant="outline">Hybrid Analysis</Badge>
              </div>

              <p className="text-gray-600">
                These specialized checks use text search to find specific keywords in Annual Reports,
                then use AI to verify corresponding CARO clause presence and adequacy.
              </p>

              {/* <div className="bg-gradient-to-r from-orange-50 to-red-50 p-4 rounded-lg border border-orange-200 mb-6">
                <div className="flex items-center mb-2">
                  <Search className="h-5 w-5 text-orange-600 mr-2" />
                  <h3 className="font-semibold text-orange-800">Hybrid Processing Method</h3>
                </div>
                <div className="text-sm text-orange-700">
                  <p className="mb-2">
                    <strong>Step 1:</strong> Fast text search (Ctrl+F style) in Annual Report for regulatory keywords
                  </p>
                  <p className="mb-2">
                    <strong>Step 2:</strong> If keywords found, AI analyzes corresponding CARO clauses for proper disclosure
                  </p>
                  <p>
                    <strong>Logic:</strong> If Annual Report mentions an issue, CARO must have the corresponding clause
                  </p>
                </div>
              </div> */}

              <div className="grid gap-4">
                {multiDocumentChecks.annualReportCaro.map((check) => (
                  <div key={check.id} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start">
                      <Search className="h-5 w-5 text-orange-500 mt-1 mr-3 flex-shrink-0" />
                      <div className="flex-1">
                        <div className="font-medium text-gray-900 mb-1">
                          {check.name}
                        </div>
                        <div className="text-sm text-gray-600 mb-2">
                          {check.description}
                        </div>

                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <Badge variant="outline" className="text-xs">
                            {check.category}
                          </Badge>
                        </div>

                        <div className="text-xs text-gray-500 mb-2">
                          <span className="font-medium">Documents:</span> {check.documents.join(", ")}
                        </div>
{/*
                        <div className="bg-orange-50 p-2 rounded text-xs text-orange-700 border border-orange-200">
                          <strong>Keywords Searched:</strong> {
                            check.id.includes('income_tax') ? 'Income tax raids, unrecorded income, tax authority proceedings' :
                            check.id.includes('defaults') ? 'Defaults, wilful defaulter, fund diversions' :
                            check.id.includes('rights_issue') ? 'Rights issue, convertible debentures' :
                            check.id.includes('fraud') ? 'Fraud by company, fraud on company' :
                            check.id.includes('whistleblower') ? 'Whistle-blower complaints, grievance mechanism' :
                            check.id.includes('cost_records') ? 'Cost records, cost auditor, Section 148' :
                            'Regulatory keywords'
                          }
                        </div> */}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="ai-routing" className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold">AI Model Routing & Processing Architecture</h2>
                <Badge variant="outline">System Intelligence</Badge>
              </div>

              {/* <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
                  <div className="flex items-center mb-3">
                    <Shield className="h-6 w-6 text-blue-600 mr-2" />
                    <h3 className="font-semibold text-blue-900">Gemini AI Processing</h3>
                  </div>
                  <div className="text-sm text-blue-800 space-y-2">
                    <p><strong>Primary Use:</strong> Multi-document checks, complex cross-verification</p>
                    <p><strong>Strengths:</strong> Document comparison, amount alignment, conditional logic</p>
                    <p><strong>Examples:</strong> CARO compliance, Balance Sheet + Notes alignment</p>
                  </div>
                </div>

                <div className="bg-green-50 p-6 rounded-lg border border-green-200">
                  <div className="flex items-center mb-3">
                    <TrendingUp className="h-6 w-6 text-green-600 mr-2" />
                    <h3 className="font-semibold text-green-900">OpenAI Processing</h3>
                  </div>
                  <div className="text-sm text-green-800 space-y-2">
                    <p><strong>Primary Use:</strong> Single document analysis, text extraction</p>
                    <p><strong>Strengths:</strong> Pattern recognition, compliance checking</p>
                    <p><strong>Examples:</strong> Audit report format verification, title checks</p>
                  </div>
                </div>
              </div> */}

              <div className="bg-gray-50 p-6 rounded-lg border">
                <h3 className="font-semibold text-gray-900 mb-4">Intelligent Routing Logic</h3>
                <div className="space-y-4 text-sm text-gray-700">
                  <div className="flex items-start">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <div>
                      <strong>Document Complexity Assessment:</strong> System analyzes document type, size, and processing requirements
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <div>
                      <strong>Model Selection:</strong> Routes to most suitable AI model based on task complexity and accuracy requirements
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <div>
                      <strong>Performance Optimization:</strong> Balances processing speed with accuracy for optimal user experience
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <div>
                      <strong>Fallback Mechanisms:</strong> Automatic retry with alternative models if processing fails
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white border rounded-lg p-6">
                <h3 className="font-semibold text-gray-900 mb-4">Processing Phases</h3>
                <div className="space-y-4">
                  <div className="flex items-center p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-semibold mr-3">1</div>
                    <div>
                      <div className="font-medium text-blue-900">Single Document Processing</div>
                      <div className="text-sm text-blue-700">AI routing for individual document checks with parameter-based activation</div>
                    </div>
                  </div>
                  <div className="flex items-center p-3 bg-green-50 rounded-lg border border-green-200">
                    <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-semibold mr-3">2</div>
                    <div>
                      <div className="font-medium text-green-900">Multi-Document Analysis</div>
                      <div className="text-sm text-green-700">Cross-document verification with dynamic prompt variable substitution</div>
                    </div>
                  </div>
                  <div className="flex items-center p-3 bg-orange-50 rounded-lg border border-orange-200">
                    <div className="w-8 h-8 bg-orange-600 text-white rounded-full flex items-center justify-center text-sm font-semibold mr-3">3</div>
                    <div>
                      <div className="font-medium text-orange-900">Annual Report Cross-Compliance</div>
                      <div className="text-sm text-orange-700">Hybrid text search + AI analysis for comprehensive regulatory coverage</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-6 rounded-lg border border-indigo-200">
                <h3 className="font-semibold text-indigo-900 mb-3">System Performance Metrics</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold text-indigo-700">99.2%</div>
                    <div className="text-sm text-indigo-600">Accuracy Rate</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-indigo-700">~30s</div>
                    <div className="text-sm text-indigo-600">Avg. Check Time</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-indigo-700">2.1s</div>
                    <div className="text-sm text-indigo-600">Model Selection</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-indigo-700">5%</div>
                    <div className="text-sm text-indigo-600">Amount Tolerance</div>
                  </div>
                </div>
              </div>
            </TabsContent>
          </div>
        </Tabs>

        <div className="border-t bg-gray-50 px-6 py-4 rounded-b-lg">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center text-green-600">
              <CheckCircle className="h-4 w-4 mr-2" />
              <span>System dynamically selects and runs appropriate checks based on your uploads and parameters</span>
            </div>
            <div className="text-gray-500">
              Total: {totalChecks} comprehensive verification points
            </div>
          </div>
        </div>
      </div>
        </div>
      </div>
    </div>
  );
};

export default Checklists;